<script setup>
import VoiceOrb from './VoiceOrb.vue'
import ScenarioTabs from './ScenarioTabs.vue'
import StarterCards from './StarterCards.vue'
import AiCaption from './AiCaption.vue'
import VoiceToast from './VoiceToast.vue'
import copyEn from './copy.en.js'
import copyKo from './copy.ko.js'
import { getVoiceDemoConfig, SCENARIO_KEYS } from './config'
import { findScenario, formatDuration, getScenarios } from './domain'
import { useVoiceSession } from './useVoiceSession'
import './voice-demo.css'

const props = defineProps({
  block: {
    type: Object,
    default: () => ({})
  }
})

const route = useRoute()
const popupOnCallEnd = computed(() => Boolean(props.block?.popupEnabled))

const openPageFormPopup = () => {
  if (typeof window === 'undefined') return
  window.__delightPendingPopupOpen = { timestamp: Date.now() }
  window.dispatchEvent(new CustomEvent('popup:open'))
}

const handleEndCall = () => {
  void session.endCall()
  if (popupOnCallEnd.value) openPageFormPopup()
}

const isKoreanRoute = computed(() => /^\/ko(?:\/|$)/.test(route.path))
const locale = computed(() => (isKoreanRoute.value ? 'ko' : 'en'))
const copy = computed(() => (isKoreanRoute.value ? copyKo : copyEn))

const config = computed(() => getVoiceDemoConfig(locale.value))
const scenarios = computed(() => getScenarios(config.value.agents, locale.value))
const requestedScenario = String(route.query.scenario || '')
const scenarioKey = ref(SCENARIO_KEYS.includes(requestedScenario) ? requestedScenario : scenarios.value[0].key)
const scenario = computed(() => findScenario(scenarios.value, scenarioKey.value))
const agentId = computed(() => scenario.value.agentId)
const orbTextures = computed(() => scenarios.value.map((item) => item.orb))

const session = useVoiceSession({ agentId, scenario, copy, locale })
const startersOpen = ref(false)

watch(() => session.connected, (isConnected) => {
  if (!isConnected) startersOpen.value = false
})

const selectScenario = (key) => {
  if (key === scenarioKey.value || session.inCall) return
  scenarioKey.value = key
}

const orbState = computed(() => {
  if (session.ended) return 'ended'
  if (session.connected) {
    if (session.reconnecting) return 'connecting'
    if (session.isSpeaking) return 'speaking'
    return 'connected'
  }
  if (session.inCall) return 'connecting'
  return 'idle'
})

const orbClass = computed(() => {
  const classes = ['orb-wrap']
  if (session.isSpeaking) classes.push('speaking')
  if (session.inCall && !session.isSpeaking) classes.push('listening')
  if (session.ended) classes.push('ended')
  return classes.join(' ')
})

const announceText = computed(() => {
  if (session.ended) return copy.value.callEnded
  if (session.connected) return copy.value.connectedAnnounce
  if (session.inCall) return copy.value.connectingAnnounce
  return ''
})

const expanded = computed(() => session.inCall || session.ended)
const micOff = computed(() => session.micMuted || session.micBlocked)
</script>

<template>
  <!-- 섹션 자체는 SSR로 렌더 — 데모 영역 높이를 미리 예약해 하이드레이션 시
       푸터가 밀려 내려가는 레이아웃 시프트(CLS)를 없앤다. 인터랙티브 내용만 ClientOnly.
       폭은 다른 블록과 동일하게 표준 .container(1616px)를 따른다 (디자인 요청). -->
  <section class="voice-demo section-mb" :id="block?.['anchorId']">
    <div class="container">
    <ClientOnly>
      <main
        class="call-page caption-ui entered"
        :data-scenario="scenario.key"
      >
        <div
          class="call-body"
          :class="{ expanded }"
        >
          <div class="call-stage">
            <span class="sr-only" aria-live="polite">{{ announceText }}</span>
            <!-- 탭은 좌측 카드 안 — 통화 전·통화 중 모두 (Figma 711:2815) -->
            <ScenarioTabs
              :scenarios="scenarios"
              :active-key="scenarioKey"
              :disabled="session.inCall"
              :switch-label="copy.switchScenario"
              @select="selectScenario"
            />
            <div class="stage-head">{{ copy.stageHead }}</div>

            <div class="stage-content">
              <div class="stage-top">
                <div
                  class="voice-meter-wrap"
                  :class="{ show: session.connected }"
                >
                  <AiCaption
                    :log="session.log"
                    :show-more-label="copy.showMore"
                    :collapse-label="copy.collapse"
                  />
                </div>

                <div :class="orbClass">
                  <VoiceOrb
                    :texture="scenario.orb"
                    :preload="orbTextures"
                    :state="orbState"
                    :output-analyser="session.outputAnalyser"
                    :input-analyser="session.inputAnalyser"
                  />
                </div>

                <div class="agent-name">{{ scenario.agent }}</div>
                <div
                  v-if="session.sequence === 'idle'"
                  class="agent-desc stage-fade"
                  :key="`desc-${scenario.key}`"
                >
                  <div
                    v-for="(line, i) in scenario.description"
                    :key="i"
                  >
                    {{ line }}
                  </div>
                </div>
                <div
                  v-else
                  class="agent-role stage-fade"
                  key="role"
                >
                  {{ scenario.role }}
                </div>
                <div class="call-time">
                  {{ formatDuration(session.elapsedSeconds) }}
                </div>
              </div>

              <div
                class="call-btn-wrap"
                :hidden="session.connected"
              >
                <p
                  v-if="session.micDenied && session.sequence === 'idle'"
                  class="call-hint"
                >
                  <span class="tip-line">{{ copy.micBlockedTitle }}</span>
                  <span class="tip-line">{{ copy.micBlockedHint }}</span>
                </p>
                <button
                  class="ctl call start"
                  :class="{ disabled: session.micDenied }"
                  :disabled="session.sequence !== 'idle' || session.starting || session.coolingDown || session.loginLocked || session.pbBuilder"
                  :aria-label="session.micDenied ? copy.requestMic : copy.startCall"
                  @click="session.micDenied ? session.requestMic() : session.startCall()"
                >
                  <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
                    <path
                      fill-rule="evenodd"
                      clip-rule="evenodd"
                      d="M18.8257 14.039C20.1189 14.4153 21.0062 15.6034 21 16.9504L20.9813 21L19.9358 20.9536C15.5762 20.76 11.3671 19.0785 8.14458 15.8555C4.92202 12.6325 3.2407 8.42285 3.04717 4.06271L3 3L7.09771 3.04724C8.42197 3.06251 9.5815 3.93964 9.95665 5.20992L10.5834 7.33224C10.9245 8.48719 10.5464 9.73439 9.62135 10.5053L7.67463 12.1278C8.2203 12.9447 8.85099 13.7169 9.56695 14.4329C10.2829 15.149 11.0549 15.7798 11.8717 16.3255L13.4977 14.374C14.2658 13.4523 15.5068 13.0732 16.6587 13.4084L18.8257 14.039ZM13.6421 17.3433C15.3126 18.1601 17.1181 18.6668 18.9796 18.8651L18.9885 16.9412C18.9905 16.4921 18.6948 16.0961 18.2637 15.9707L16.0967 15.3401C15.7127 15.2283 15.2991 15.3547 15.0431 15.662L13.6421 17.3433ZM6.65698 10.3572L8.33359 8.95983C8.64193 8.70284 8.76798 8.28711 8.65428 7.90213L8.0275 5.77981C7.90245 5.35638 7.51594 5.06401 7.07452 5.05892L5.13729 5.03658C5.33684 6.89217 5.84283 8.69173 6.65698 10.3572Z"
                      fill="currentColor"
                    />
                  </svg>
                  <span
                    v-if="session.inCall"
                    class="tip"
                  >{{ copy.connecting }}</span>
                </button>
              </div>
            </div>

            <div
              v-if="session.ended"
              class="call-ended-badge"
            >
              {{ copy.callEnded }}
            </div>

            <div class="stage-bottom">
              <div
                class="incall-row"
                :hidden="!session.connected"
              >
                <button
                  class="log-fab"
                  :aria-label="copy.showExamples"
                  @click="startersOpen = !startersOpen"
                >
                  <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
                    <path d="M12 5C11.3169 5 10.75 5.57017 10.75 6.24269C10.75 6.92982 11.3169 7.5 12 7.5C12.6831 7.5 13.25 6.92982 13.25 6.24269C13.25 5.57017 12.6831 5 12 5Z" fill="currentColor" />
                    <path d="M11 15V9H13V15H11Z" fill="currentColor" />
                    <path fill-rule="evenodd" clip-rule="evenodd" d="M4.5 2C3.11929 2 2 3.11929 2 4.5V22L6 18H19.5C20.8807 18 22 16.8807 22 15.5L22 4.5C22 3.11929 20.8807 2 19.5 2H4.5ZM19.5 16C19.7761 16 20 15.7761 20 15.5L20 4.5C20 4.22386 19.7761 4 19.5 4H4.5C4.22386 4 4 4.22386 4 4.5V17.1716L5.17157 16H19.5Z" fill="currentColor" />
                  </svg>
                </button>
                <button
                  class="incall-mute"
                  :class="{ muted: micOff }"
                  :aria-label="session.micBlocked ? copy.requestMic : session.micMuted ? copy.unmute : copy.mute"
                  @click="session.micBlocked ? session.requestMic() : session.toggleMute()"
                >
                  <span
                    v-if="session.micBlocked"
                    class="mic-tip"
                  >
                    <span class="tip-line">{{ copy.micBlockedTitle }}</span>
                    <span class="tip-line">{{ copy.micBlockedHint }}</span>
                  </span>
                  <svg
                    v-if="micOff"
                    viewBox="0 0 24 24"
                    fill="none"
                    aria-hidden="true"
                  >
                    <path d="M19 12C19 15.5264 16.3921 18.4414 13 18.9268V20H16V22H8V20H11V18.9268C9.32387 18.6869 7.83976 17.8534 6.7666 16.6465L8.18359 15.2295C9.10076 16.3123 10.4699 17 12 17C14.7614 17 17 14.7614 17 12H19ZM16 12C16 14.2091 14.2091 16 12 16C10.746 16 9.62704 15.4226 8.89355 14.5195L10.3242 13.0889C10.681 13.6368 11.2974 14 12 14C13.1046 14 14 13.1046 14 12V9.41406L16 7.41406V12ZM7 12C7 12.4819 7.06904 12.9477 7.19629 13.3887L5.64648 14.9385C5.23231 14.045 5 13.0499 5 12H7ZM12 2C13.7823 2 15.2916 3.16586 15.8086 4.77637L14 6.58594V6C14 4.89543 13.1046 4 12 4C10.8954 4 10 4.89543 10 6V10.5859L8.03711 12.5479C8.0217 12.4353 8.01073 12.3214 8.00488 12.2061L8 12V6C8 3.79086 9.79086 2 12 2Z" fill="currentColor" />
                    <path d="M19.2929 3.29295C19.6834 2.90243 20.3164 2.90243 20.707 3.29295C21.0975 3.68348 21.0975 4.31649 20.707 4.70702L4.70696 20.707C4.31643 21.0975 3.68342 21.0975 3.29289 20.707C2.90237 20.3165 2.90237 19.6835 3.29289 19.293L19.2929 3.29295Z" fill="currentColor" />
                  </svg>
                  <svg
                    v-else
                    viewBox="0 0 24 24"
                    fill="none"
                    aria-hidden="true"
                  >
                    <path d="M13 21L11 21L11 18L13 18L13 21Z" fill="currentColor" />
                    <path d="M12 19C8.13401 19 5 15.866 5 12L7 12C7 14.7614 9.23858 17 12 17C14.7614 17 17 14.7614 17 12L19 12C19 15.866 15.866 19 12 19Z" fill="currentColor" />
                    <path d="M14 12V6C14 4.89543 13.1046 4 12 4C10.8954 4 10 4.89543 10 6V12C10 13.1046 10.8954 14 12 14V16C9.85996 16 8.11211 14.3194 8.00488 12.2061L8 12V6C8 3.79086 9.79086 2 12 2C14.2091 2 16 3.79086 16 6V12C16 14.2091 14.2091 16 12 16V14C13.1046 14 14 13.1046 14 12Z" fill="currentColor" />
                    <path d="M16 20V22H8V20H16Z" fill="currentColor" />
                  </svg>
                </button>
                <button
                  class="incall-end"
                  :aria-label="copy.endCall"
                  @click="handleEndCall"
                >
                  <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
                    <path d="M19.2929 3.29295C19.6834 2.90243 20.3164 2.90243 20.707 3.29295C21.0975 3.68348 21.0975 4.31649 20.707 4.70702L4.70696 20.707C4.31643 21.0975 3.68342 21.0975 3.29289 20.707C2.90237 20.3165 2.90237 19.6835 3.29289 19.293L19.2929 3.29295Z" fill="currentColor" />
                    <path d="M13.498 14.374C14.266 13.4525 15.5065 13.0733 16.6582 13.4082L18.8252 14.0391C20.1184 14.4154 21.0061 15.6032 21 16.9502L20.9814 21L19.9355 20.9531C15.5761 20.7595 11.367 19.0784 8.14453 15.8555C8.04646 15.7574 7.94971 15.6585 7.85449 15.5586L9.27734 14.1357C9.37221 14.2359 9.46931 14.3345 9.56738 14.4326C10.2833 15.1487 11.0553 15.7795 11.8721 16.3252L13.498 14.374ZM16.0967 15.3398C15.7127 15.2281 15.299 15.3549 15.043 15.6621L13.6426 17.3438C15.3129 18.1604 17.1182 18.667 18.9795 18.8652L18.9883 16.9414C18.9903 16.4924 18.6948 16.0962 18.2637 15.9707L16.0967 15.3398ZM7.09766 3.04688C8.42192 3.06214 9.58188 3.93968 9.95703 5.20996L10.583 7.33203C10.9241 8.48694 10.5461 9.73392 9.62109 10.5049L7.6748 12.1279C7.77863 12.2834 7.88611 12.4368 7.99609 12.5889L6.55762 14.0273C4.36678 11.1146 3.20564 7.63941 3.04688 4.0625L3 3L7.09766 3.04688ZM5.1377 5.03613C5.33725 6.89172 5.84307 8.69196 6.65723 10.3574L8.33398 8.95996C8.64215 8.70301 8.76787 8.2872 8.6543 7.90234L8.02734 5.78027C7.90229 5.35685 7.51564 5.06368 7.07422 5.05859L5.1377 5.03613Z" fill="currentColor" />
                  </svg>
                </button>
              </div>
            </div>
          </div>

          <!-- 통화 전 기본 화면(디자인 신규): 우측 소개 패널 — 통화 시작 시 사라지고
               기존과 동일하게 대화 기록 패널이 펼쳐진다 -->
          <aside class="intro-panel">
            <div class="intro-copy">
              <h3 class="intro-title">
                <span
                  v-for="(line, i) in copy.introTitle"
                  :key="i"
                  class="intro-title-line"
                >{{ line }}</span>
              </h3>
              <p class="intro-desc">{{ copy.introBody }}</p>
            </div>
            <p class="intro-privacy">{{ copy.disclaimer }}</p>
          </aside>

          <StarterCards
            :chips="scenario.chips"
            :expanded="expanded"
            :open="startersOpen"
            :heading="copy.startersHeading"
            :close-label="copy.close"
            @close="startersOpen = false"
          />
        </div>
      </main>

      <VoiceToast />
    </ClientOnly>
    </div>
  </section>
</template>

<style scoped>
.sr-only {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}
</style>
